import React, { Children } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2Icon } from 'lucide-react';
import { Container } from '../../components/ui/Container';
import { Button } from '../../components/ui/Button';
import { SubpageHero } from '../../components/sections/subpages/SubpageHero';
import { PatternDecor } from '../../components/ui/PatternDecor';
export function DonateForChildren() {
  return <>
      <SubpageHero eyebrow="Education as long-term protection" title="Donate for Children" paragraphs={['Muslim children growing up outside the Muslim world face unique challenges related to identity, belonging, and values.', 'Your contribution funds structured, culturally sensitive education that protects identity and builds confidence for the long term.']} primaryCta={{
      label: 'Donate',
      to: '/en/contact'
    }} secondaryCta={{
      label: 'Scholarships Program',
      to: '/en/partnership/scholarships'
    }} image="https://cdn.magicpatterns.com/uploads/kAr5kYqxVPDypDnS73jsRc/Donate.jpg" imageAlt="A smiling teacher embraced by a group of happy Muslim children holding their artwork in a bright classroom" compact masked data-id="element-4780" data-mp="3c5k72kzfhn0h" />

      {/* Protecting Identity Through Education */}
      <section className="relative py-16 md:py-24 overflow-hidden isolate bg-sage-50/60" data-id="element-4781" data-mp="pmdhg4hbmtjz">
        <PatternDecor side="left" y="top" size="lg" data-id="element-4782" data-mp="ul6slq94y8gz" />
        <Container data-id="element-4783" data-mp="ukopc9o22tzk">
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-4784" data-mp="uj463g76yxh0">
            Protecting identity through education
          </h2>
          <p className="mt-5 text-ink-700 leading-relaxed" data-id="element-4785" data-mp="km7vn4xqg19t">
            Through structured educational support, we help children:
          </p>
          <ul className="mt-6 grid sm:grid-cols-2 gap-x-10 gap-y-3" data-id="element-4786" data-mp="30g9jqy4b2luw">
            {['Grow with confidence and emotional stability', 'Preserve Islamic values and cultural identity', 'Stay focused, integrated, and balanced in modern life', 'Develop the inner foundation to thrive in any environment'].map(item => <li key={item} className="flex items-start gap-3 text-ink-700" data-id="element-4787" data-mp="122va61mpp2bv">
                <CheckCircle2Icon size={16} className="text-forest-700 mt-1 flex-shrink-0" strokeWidth={1.8} data-id="element-4788" data-mp="x4tr3gpad30e" />
                {item}
              </li>)}
          </ul>
        </Container>
      </section>

      {/* Where your donation goes */}
      <section className="py-16 md:py-24 bg-white/60" data-id="element-4789" data-mp="j63tlzyk11bi">
        <Container data-id="element-4790" data-mp="e7k79yg1tnim">
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-4791" data-mp="e82a5dx7uzzi">
            Where your donation goes
          </h2>
          <p className="mt-5 text-ink-700 leading-relaxed" data-id="element-4792" data-mp="2dhikik3ax6su">
            Every contribution supports structured, accountable programs:
          </p>
          <div className="mt-10 grid md:grid-cols-2 gap-5" data-id="element-4793" data-mp="2g39lco1pcsaf">
            {[{
            title: 'Educational Programs for Children',
            body: 'Direct delivery of age-appropriate learning in identity, values, and cultural awareness.'
          }, {
            title: 'Scholarships for Families in Need',
            body: 'Removing financial barriers so children from all backgrounds can access structured learning.'
          }, {
            title: 'Learning Materials & Online Access',
            body: 'Curriculum, digital platforms, and resources designed for families living abroad.'
          }, {
            title: 'Safeguarding & Quality Assurance',
            body: 'Ethical standards, child protection, and ongoing review to keep delivery responsible.'
          }].map((card, i) => <motion.div key={card.title} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.5,
            delay: i * 0.06
          }} className={`rounded-2xl border border-ink-900/5 p-6 shadow-soft ${['bg-sage-100', 'bg-[#F7EDE9]', 'bg-sand-100', 'bg-[#EDF2F7]'][i]}`} data-id="element-4794" data-mp="3ng1m7eq7jd2j" data-mpi={i}>
                <h3 className="font-display text-lg text-ink-900" data-id="element-4795" data-mp="hlfsvzbntkuu">
                  {card.title}
                </h3>
                <p className="mt-3 text-ink-700 text-sm leading-relaxed" data-id="element-4796" data-mp="cnxuh2v9qo13">
                  {card.body}
                </p>
              </motion.div>)}
          </div>
        </Container>
      </section>

      {/* Transparency */}
      <section className="relative py-16 md:py-24 overflow-hidden isolate bg-sand-50/60" data-id="element-4797" data-mp="167dtddz72cx3">
        <PatternDecor side="right" y="bottom" size="md" data-id="element-4798" data-mp="165j8fznxakt0" />
        <Container data-id="element-4799" data-mp="1a7o04sut97d0">
          <h3 className="font-display text-lg text-ink-900" data-id="element-4800" data-mp="1a861g3wbybrs">
            Accountability & transparency
          </h3>
          <p className="mt-4 text-ink-700 leading-relaxed" data-id="element-4801" data-mp="wbk6b0nun112">
            Donations are administered by MTÜ IILH (International Islamic Learning Hub). Professional delivery is provided by partners under formal
            service agreements. All donors receive structured reporting on
            impact, fund allocation, and governance.
          </p>
        </Container>
      </section>

      {/* Ready to support CTA */}
      <section className="py-16 md:py-24 bg-white/60" data-id="element-4802" data-mp="ihjfqu71wkbs">
        <Container data-id="element-4803" data-mp="ii1bzqtpqyjp">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8" data-id="element-4804" data-mp="iff2vmr6cazd">
            <div className="max-w-xl" data-id="element-4805" data-mp="3mlltww97msl">
              <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-4806" data-mp="8jva397wpseu">
                Ready to support?
              </h2>
              <p className="mt-4 text-ink-700" data-id="element-4807" data-mp="3llpdchd4qiqk">
                Choose the most relevant option — donation, scholarships, or
                institutional partnership. We'll respond quickly with next
                steps.
              </p>
            </div>
            <div className="flex flex-col items-start md:items-end gap-3" data-id="element-4808" data-mp="36w9pb94zgrek">
              <p className="max-w-sm text-sm text-ink-500 leading-relaxed md:text-right" data-id="element-4809" data-mp="8dkwwzo25lgy">
                Please note: We accept Sadaqah and corporate grants; Zakat is
                not accepted at this stage.
              </p>
              <div className="flex flex-wrap gap-3" data-id="element-4810" data-mp="3uk1cj7s2aoum">
                <Link to="/en/contact#general-inquiry" data-id="element-4811" data-mp="3umvtgpbv9rja">
                  <Button variant="primary" data-id="element-4812" data-mp="3pl6jze4allxb">Donate</Button>
                </Link>
                <Link to="/en/contact#general-inquiry" data-id="element-4813" data-mp="2j65u9q5v1zz6">
                  <Button variant="secondary" data-id="element-4814" data-mp="2j3jgh8sdmo8i">Become a Partner</Button>
                </Link>
              </div>
            </div>
          </div>
        </Container>
      </section>
    </>;
}